import React, { useEffect, useState, useMemo, useContext } from "react";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import { graphql } from "react-relay";
import { useLazyLoadQuery } from "react-relay/hooks";
import { Typography, Button, Tooltip, SelectChangeEvent } from "@mui/material";

import ChildTaskTable from "../../../components/common/ChildTaskTable";
import { GeneralTaskChildrenTabQuery } from "./__generated__/GeneralTaskChildrenTabQuery.graphql";
import { FilteredArguments, ValidatedChildren, SweepArguments, GeneralTaskParams } from "../generalTask.types";
import { GeneralTaskPageQuery$data } from "../__generated__/GeneralTaskPageQuery.graphql";
import {
  applyChildTaskFilter,
  updateFilteredArguments,
  validateChildTasks,
  getChildTaskIdArray,
  getRuptureSetIdArray,
  maxLength,
  getGeneralTaskDetailsFromQueryResponse,
  tidySweptArguments,
} from "../generalTask.service";

import ControlsBar from "../../../components/common/ControlsBar";
import { useShortcut } from "../../../hooks/useShortcut";
import SweepArgumentFilter from "../SweepArgumentFilter";
import InversionSolutionDiagnosticContainer from "../InversionSolutionDiagnosticContainer";
import RuptureSetDiagnosticContainer from "../RuptureSetDiagnosticContainer";
import GeneralTaskDetailDrawer from "../../../components/diagnosticReportCard/GeneralTaskDetailDrawer";
import DialogAlert from "../../../components/common/DialogAlert";
import CentredCircularProgress from "../../../components/common/CentredCircularProgress";
import DisableHotkeyContext from "../../../contexts/DisableHotkeyContext";

const StyledButton = styled(Button)(() => ({
  margin: 10,
}));

const ControlsContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
}));

const FilterContainer = styled("div")(() => ({
  width: "100%",
  display: "flex",
  flexWrap: "wrap",
  paddingLeft: 10,
}));

interface GeneralTaskChildrenTabProps {
  readonly sweepArgs?: SweepArguments;
  generalTaskData?: GeneralTaskPageQuery$data;
}

const GeneralTaskChildrenTab: React.FC<GeneralTaskChildrenTabProps> = ({
  generalTaskData,
  sweepArgs,
}: GeneralTaskChildrenTabProps) => {
  const { id } = useParams<GeneralTaskParams>();

  // Must be trimmed the same way as GeneralTaskPage does, so both queries share a
  // single `node(id:)` root key in the Relay store.
  const data = useLazyLoadQuery<GeneralTaskChildrenTabQuery>(generalTaskChildrenTabQuery, {
    id: (id ?? "").trim(),
  });
  const childTasks = useMemo(() => validateChildTasks(data), [data]);

  const [filteredArguments, setFilteredArguments] = useState<FilteredArguments>({ data: [] });
  const [filteredChildren, setFilteredChildren] = useState<ValidatedChildren>(childTasks);
  const [isOpenQuakeHazardTask, setIsOpenQuakeHazardTask] = useState<boolean>(false);
  const [isRuptureGenerationTask, setIsRuptureGenerationTask] = useState<boolean>(false);
  const [reportTab, setReportTab] = useState<number>(0);
  const [showFilter, setShowFilter] = useState(true);
  const [showList, setShowList] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  const { disableHotkey } = useContext(DisableHotkeyContext);

  const cleanSweepArgs = useMemo(() => {
    return tidySweptArguments(sweepArgs, childTasks);
  }, [sweepArgs, childTasks]);

  const handleViewChange = () => {
    if (showList) {
      if (filteredChildren.data && filteredChildren.data?.length > 0 && filteredChildren.data?.length <= maxLength) {
        setShowList((v) => !v);
      } else {
        setOpenAlert(true);
      }
    } else {
      setShowList((v) => !v);
    }
  };

  useEffect(() => {
    if (
      filteredChildren?.data?.length &&
      filteredChildren.data.length > 0 &&
      filteredChildren.data[0].__typename === "OpenquakeHazardTask"
    ) {
      setIsOpenQuakeHazardTask(true);
    }
    setIsRuptureGenerationTask(
      !!filteredChildren?.data?.length &&
        filteredChildren.data.length > 0 &&
        filteredChildren.data[0].__typename === "RuptureGenerationTask",
    );
  }, [filteredChildren]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const newFilteredArguments = updateFilteredArguments(
      filteredArguments,
      event.target.value as string[],
      event.target.name as string,
    );
    setFilteredArguments(newFilteredArguments);
  };

  const applyFilter = () => {
    const filtered = applyChildTaskFilter(childTasks, filteredArguments);
    setFilteredChildren(filtered);
    if (filtered.data && filtered.data?.length > maxLength && !showList) {
      setOpenAlert(true);
    } else {
      setFilteredChildren(filtered);
    }
  };

  useShortcut(() => setShowFilter((v) => !v), ["f"], disableHotkey);
  useShortcut(() => setOpenDrawer((v) => !v), ["d"], disableHotkey);

  if (!data?.node) {
    return (
      <Typography variant="h5" gutterBottom>
        General Task: Id Not Found
      </Typography>
    );
  }

  return (
    <>
      <ControlsContainer>
        {!isOpenQuakeHazardTask && cleanSweepArgs && (
          <Tooltip title="use (f/F) to open/close filters">
            <StyledButton color="inherit" variant="contained" onClick={() => setShowFilter((v) => !v)}>
              <span>
                Filter&nbsp;
                {`${
                  filteredArguments.data.length ? (filteredChildren.data?.length ?? 0) : (childTasks.data?.length ?? 0)
                }/${childTasks.data?.length ?? 0}`}
              </span>
            </StyledButton>
          </Tooltip>
        )}
        <Tooltip title="use (d/D) to open/close details">
          <StyledButton
            color="inherit"
            variant="contained"
            onClick={() => setOpenDrawer((v) => !v)}
            style={isOpenQuakeHazardTask ? { marginBottom: "16px" } : {}}
          >
            Details
          </StyledButton>
        </Tooltip>
        <Tooltip title="use (s/S) to toggle between views">
          <StyledButton color="inherit" variant="contained" onClick={handleViewChange}>
            {showList ? "Show Report" : "Show List"}
          </StyledButton>
        </Tooltip>
      </ControlsContainer>
      {!isOpenQuakeHazardTask && (
        <FilterContainer sx={showFilter ? {} : { display: "none" }}>
          <ControlsBar>
            {cleanSweepArgs?.map((argument) => (
              <SweepArgumentFilter key={`${argument?.k}-filter`} argument={argument} onChange={handleChange} />
            ))}
            {cleanSweepArgs && (
              <Button color="inherit" variant="contained" onClick={applyFilter}>
                Apply
              </Button>
            )}
          </ControlsBar>
        </FilterContainer>
      )}
      <React.Suspense fallback={<CentredCircularProgress />}>
        {showList ? (
          <div>
            {filteredChildren.data?.length ? (
              <ChildTaskTable data={filteredChildren} />
            ) : data?.node?.children?.edges && data?.node?.children?.edges.length > 0 ? (
              <ChildTaskTable data={childTasks} />
            ) : null}
          </div>
        ) : isRuptureGenerationTask ? (
          <RuptureSetDiagnosticContainer
            sweepArgs={cleanSweepArgs}
            ids={
              filteredChildren.data?.length ? getRuptureSetIdArray(filteredChildren) : getRuptureSetIdArray(childTasks)
            }
          />
        ) : (
          <InversionSolutionDiagnosticContainer
            sweepArgs={cleanSweepArgs}
            modelType={data?.node?.model_type as string}
            ids={
              filteredChildren.data?.length ? getChildTaskIdArray(filteredChildren) : getChildTaskIdArray(childTasks)
            }
            reportTab={reportTab}
            setReportTab={setReportTab}
          />
        )}
      </React.Suspense>
      {generalTaskData ? (
        <GeneralTaskDetailDrawer
          generalTaskDetails={getGeneralTaskDetailsFromQueryResponse(generalTaskData)}
          openDrawer={openDrawer}
        />
      ) : null}
      {/* <CommonModal
        input={false}
        title="Share with this url"
        openModal={showShare}
        text={getSharableUrl()}
        handleClose={() => setShowShare(false)}
      /> */}
      {/* <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={openNotification}
        onClose={handleCloseNotification}
      >
        <Alert variant="filled" severity="warning">
          Sorry, this URL is invalid. The clipBoard state cannot be applied.
        </Alert>
      </Snackbar> */}
      {openAlert ? (
        <DialogAlert
          open={openAlert}
          title="Cannot Query Reports"
          text={`Reports cannot be queried when the list of filtered child tasks is over ${maxLength}.`}
          handleClose={() => setOpenAlert(false)}
        />
      ) : null}
    </>
  );
};

export default GeneralTaskChildrenTab;

const generalTaskChildrenTabQuery = graphql`
  query GeneralTaskChildrenTabQuery($id: ID!) {
    node(id: $id) {
      ... on GeneralTask {
        id
        model_type
        children {
          edges {
            node {
              child {
                __typename
                ... on Node {
                  id
                }
                ... on AutomationTask {
                  task_type
                  inversion_solution {
                    __typename
                    ... on Node {
                      id
                    }
                    ... on InversionSolution {
                      id
                      mfd_table {
                        id
                      }
                    }
                  }
                }
                ... on RuptureGenerationTask {
                  task_type
                  files {
                    edges {
                      node {
                        file {
                          __typename
                          ... on Node {
                            id
                          }
                          ... on RuptureSet {
                            id
                            meta {
                              k
                              v
                            }
                          }
                        }
                      }
                    }
                  }
                }
                ... on AutomationTaskInterface {
                  state
                  result
                  created
                  duration
                  arguments {
                    k
                    v
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
